import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CoursesAdminService } from "@/services";
import { CourseAdmin, CourseDto } from "@/shared/types/Course";
import { PaginatedList } from "@/shared/types";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

/**
 * Hook para LISTAR cursos (GET)
 *
 * STATE MANAGEMENT POLICY: Uses React Query as single source of truth.
 * No Redux synchronization - React Query cache is shared across components.
 */
export const useAdminCourses = (
  page: number,
  pageSize: number,
  search: string,
) => {
  const {
    data: response,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ["admin-courses", page, pageSize, search],
    queryFn: ({ signal }) =>
      CoursesAdminService.getCoursesPaginated(page, pageSize, search, signal),
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

  return {
    courses: response?.list ?? [],
    total: response?.total ?? 0,
    isLoading: isFetching,
    isError,
    error,
  };
};

interface useCourseByIdResponse {
  course: CourseAdmin | null;
  isLoading: boolean;
  hasError: boolean;
  error: Error | null;
}

export const useCourseById = (
  courseId: number | null,
): useCourseByIdResponse => {
  const {
    data: courseResponse,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ["course", courseId],
    queryFn: ({ signal }) => {
      if (!courseId) return null;
      return CoursesAdminService.getCourseById(courseId, signal);
    },
    enabled: !!courseId,
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

  return {
    course: courseResponse || null,
    isLoading: isFetching,
    hasError: isError,
    error: error as Error | null,
  };
};

export const useCreateCourse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (courseData: CourseDto) => {
      return CoursesAdminService.CreateCourse(courseData);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["course-image-urls"] });
      queryClient.setQueriesData(
        { queryKey: ["admin-courses"] },
        (oldData: unknown) => {
          const typedOldData = oldData as
            | PaginatedList<CourseAdmin>
            | undefined;
          if (!typedOldData || !typedOldData.list) return typedOldData;
          return {
            ...typedOldData,
            list: [data, ...typedOldData.list],
            total: (typedOldData.total || 0) + 1,
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: ["admin-courses-active"] });

      toast.success("Curso creado exitosamente");
    },
    onError: (error) => {
      logger.error("Error creating course:", error);
      toast.error("Error al crear el curso");
    },
  });
};

export const useUpdateCourse = (courseId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (courseData: CourseDto) => {
      return CoursesAdminService.updateCourse(courseData, courseId);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["course-image-urls"] });
      queryClient.setQueriesData(
        { queryKey: ["admin-courses"] },
        (oldData: unknown) => {
          const typedOldData = oldData as
            | PaginatedList<CourseAdmin>
            | undefined;
          if (!typedOldData || !typedOldData.list) return typedOldData;
          return {
            ...typedOldData,
            list: typedOldData.list.map((course: CourseAdmin) =>
              course.id === courseId
                ? { ...course, ...variables, id: courseId }
                : course,
            ),
          };
        },
      );
      toast.success("Curso actualizado exitosamente");
    },
    onError: (error) => {
      logger.error("Error updating course:", error);
      toast.error("Error al actualizar el curso");
    },
  });
};

export const useDeleteCourse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (courseId: number) =>
      CoursesAdminService.deleteCourse(courseId),
    onSuccess: (_data, courseId) => {
      queryClient.setQueriesData(
        { queryKey: ["admin-courses"] },
        (oldData: unknown) => {
          const typedOldData = oldData as
            | PaginatedList<CourseAdmin>
            | undefined;
          if (!typedOldData || !typedOldData.list) return typedOldData;

          return {
            ...typedOldData,
            list: typedOldData.list.filter((course) => course.id !== courseId),
            total: typedOldData.total - 1,
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: ["admin-courses-active"] });

      toast.success("Curso eliminado correctamente");
    },
    onError: (error) => {
      logger.error("Error deleting course:", error);
      toast.error("Error al eliminar el curso");
    },
  });
};

// Hook para obtener URLs de imágenes de cursos por IDs
export const useCourseImageUrls = (
  courseIds: number[],
  expiryMinutes: number = 60,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ["course-image-urls", courseIds, expiryMinutes],
    queryFn: () => {
      if (!courseIds.length) return Promise.resolve({});
      return CoursesAdminService.getCourseImageUrls(courseIds, expiryMinutes);
    },
    enabled,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
};
